import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Formik, FormikHelpers } from "formik";
import React, { useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router";
import * as Yup from "yup";

import LoggedInUserApi from "../../../api/LoggedInUserApi";
import { appConstants } from "../../../config/app-constants";
import { EToastvariant, IChangePasswordForm } from "../../../config/interfaces";
import { useAppDispatch } from "../../../redux/Store";
import { showToastMsg } from "../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../services";
import FASvgIcon from "../../FASvgIcon";
import ScrollToFirstError from "../../ScrollToFirstError";
import PasswordHintOverlay from "../../overlay/PasswordHintOverlay";
import { ButtonSpinner } from "../../placeholders";

const initialValues: IChangePasswordForm = {
  oldPassword: "",
  newPassword: "",
  confirmNewPassword: "",
  logOutPreviousSessions: false
};

const validationSchema = Yup.object({
  oldPassword: Yup.string()
    .trim()
    .matches(appConstants.PASSWORD_REGEX, "Invalid password")
    .required("Old password is required"),
  newPassword: Yup.string()
    .trim()
    .matches(appConstants.PASSWORD_REGEX, "Invalid password")
    .test("passwords-match", "New password should not match old password", function (value) {
      return value !== this.parent.oldPassword;
    })
    .required("New password is required"),
  confirmNewPassword: Yup.string()
    .trim()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Repeat new password is required")
});

const ChangePassword: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const pwdOverlayTargetRef = useRef<HTMLInputElement | null>(null);

  const [showHintOverlay, setShowHintOverlay] = useState<boolean>(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPwd, setShowConfirmNewPwd] = useState(false);

  const toggleShowOldPassword = () => {
    setShowOldPassword(!showOldPassword);
  };

  const toggleShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleShowConfirmNewPwd = () => {
    setShowConfirmNewPwd(!showConfirmNewPwd);
  };

  const handleChangePwdFormSubmit = async (
    values: IChangePasswordForm,
    { setSubmitting }: FormikHelpers<IChangePasswordForm>
  ): Promise<void> => {
    try {
      setSubmitting(true);
      const _values = {
        oldPassword: values?.oldPassword,
        newPassword: values?.newPassword,
        logOutPreviousSessions: values?.logOutPreviousSessions
      };
      const userApi = LoggedInUserApi.getInstance();
      await userApi.changeMyPassword(_values);

      if (values.logOutPreviousSessions) {
        UtilService.handleLogoutOnUnauthorized();
      } else {
        dispatch(
          showToastMsg({
            isVisible: true,
            title: "Updated",
            message: "Password updated successfully",
            variant: EToastvariant.Success
          })
        );
      }
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card className="form-card">
        <Card.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleChangePwdFormSubmit}
          >
            {({
              handleChange,
              handleBlur,
              handleSubmit,
              validateForm,
              values,
              errors,
              touched,
              isValid,
              setFieldValue,
              submitCount,
              isSubmitting
            }) => (
              <Form
                onSubmit={async (event) => {
                  event.preventDefault();
                  await validateForm();
                  handleSubmit();
                }}
              >
                <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
                <Row className="align-items-center">
                  <Col sm={4}>
                    <Form.Label className="required-field inline-label">Old Password</Form.Label>
                  </Col>
                  <Col>
                    <Form.Group className="form-group" controlId="oldPassword">
                      <div className="field-has-icon">
                        <Form.Control
                          type={showOldPassword ? "text" : "password"}
                          autoComplete="false"
                          placeholder="***********"
                          name="oldPassword"
                          value={values.oldPassword}
                          onBlur={handleBlur("oldPassword")}
                          onChange={handleChange("oldPassword")}
                          isInvalid={Boolean(errors.oldPassword) && touched.oldPassword}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.oldPassword}
                        </Form.Control.Feedback>
                        <Button
                          variant="transparent"
                          className="field-icon"
                          onClick={toggleShowOldPassword}
                        >
                          <FASvgIcon icon={showOldPassword ? faEye : faEyeSlash} />
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="align-items-center">
                  <Col sm={4}>
                    <Form.Label className="required-field inline-label">New Password</Form.Label>
                  </Col>
                  <Col>
                    <Form.Group className="form-group" controlId="newPassword">
                      <div className="field-has-icon" ref={pwdOverlayTargetRef}>
                        <Form.Control
                          type={showNewPassword ? "text" : "password"}
                          name="newPassword"
                          isInvalid={!!errors.newPassword && !!touched.newPassword}
                          value={values.newPassword}
                          placeholder="***********"
                          onChange={handleChange}
                          onFocus={() => {
                            setShowHintOverlay(true);
                          }}
                          onBlur={(e) => {
                            handleBlur(e);
                            setShowHintOverlay(false);
                          }}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.newPassword}
                        </Form.Control.Feedback>
                        <Button
                          variant="btn"
                          className="field-icon"
                          onClick={toggleShowNewPassword}
                        >
                          <FASvgIcon icon={showNewPassword ? faEye : faEyeSlash} />
                        </Button>
                      </div>
                      <PasswordHintOverlay show={showHintOverlay} targetRef={pwdOverlayTargetRef} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="align-items-center">
                  <Col sm={4}>
                    <Form.Label className="required-field inline-label">
                      Repeat New Password
                    </Form.Label>
                  </Col>
                  <Col>
                    <Form.Group className="form-group" controlId="confirmNewPassword">
                      <div className="field-has-icon">
                        <Form.Control
                          type={showConfirmNewPwd ? "text" : "password"}
                          name="confirmNewPassword"
                          onBlur={handleBlur}
                          isInvalid={!!errors.confirmNewPassword && !!touched.confirmNewPassword}
                          value={values.confirmNewPassword}
                          placeholder="***********"
                          onChange={handleChange}
                          autoComplete="confirmNewPassword"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.confirmNewPassword}
                        </Form.Control.Feedback>
                        <Button
                          variant="btn"
                          className="field-icon"
                          onClick={toggleShowConfirmNewPwd}
                        >
                          <FASvgIcon icon={showConfirmNewPwd ? faEye : faEyeSlash} />
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col sm={{ span: "auto", offset: 4 }}>
                    <Form.Group className="form-group">
                      <Form.Check
                        role="button"
                        type="checkbox"
                        id="logOutPreviousSessions"
                        className="align-items-center"
                      >
                        <Form.Check.Input
                          type="checkbox"
                          name="logOutPreviousSessions"
                          checked={values.logOutPreviousSessions}
                          onChange={(e) => {
                            setFieldValue("logOutPreviousSessions", e.target.checked);
                          }}
                          isInvalid={
                            Boolean(errors.logOutPreviousSessions) && touched.logOutPreviousSessions
                          }
                        />
                        <Form.Check.Label>Sign out of all devices</Form.Check.Label>
                        <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                      </Form.Check>
                    </Form.Group>
                  </Col>
                </Row>
                <div className="form-btns-wrap">
                  {/* <Button
                    size="lg"
                    variant="outline-greyDark"
                    className="btn-w-120"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button> */}
                  <Button size="lg" variant="primary" className="btn-w-160" type="submit">
                    {!isSubmitting ? "Update" : <ButtonSpinner />}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </>
  );
};

export default ChangePassword;
